'use client';

import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import styles from './KioskCaseStudy.module.css';
import type { Locale } from '@/lib/i18n';
import { getIntegrationOrPartnerPath } from '@/lib/routes';

interface Integration {
  name: string;
  logo: string;
  category: string;
}

interface CaseStudyItem {
  company: string;
  solution: string;
  location: string;
  videoWebm: string;
  videoMp4: string;
  description: string;
  integrations: Integration[];
}

export interface KioskCaseStudyProps {
  badge: string;
  title: string;
  subtitle: string;
  cases: CaseStudyItem[];
  labels: {
    client: string;
    solution: string;
    location: string;
    integratedWith: string;
  };
  altTexts?: Record<string, string>;
  locale?: Locale;
}

/**
 * KioskCaseStudy Component
 * Displays case studies in alternating layout
 */
export function KioskCaseStudy({ badge, title, subtitle, cases, labels, altTexts, locale = 'en' }: KioskCaseStudyProps) {
  const [isMounted, setIsMounted] = useState(false);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Lazy load videos when they're near viewport
  // ✅ PERFORMANCE: Balanced rootMargin for smooth UX
  useEffect(() => {
    if (!isMounted) return;

    // Detect mobile - larger margin for smoother experience
    const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const rootMargin = isMobileDevice ? '200px' : '300px';

    const observers = videoRefs.current.map((video, index) => {
      if (!video) return null;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const videoElement = entry.target as HTMLVideoElement;
              // Load video by setting src from data attributes
              const sources = videoElement.querySelectorAll('source');
              sources.forEach((source) => {
                if (!source.src && source.dataset.src) {
                  source.src = source.dataset.src;
                }
              });
              videoElement.load();
              observer.unobserve(videoElement);
            }
          });
        },
        { rootMargin }  // 200px mobile, 300px desktop - smoother UX
      );

      observer.observe(video);
      return observer;
    });

    return () => {
      observers.forEach((observer) => observer?.disconnect());
    };
  }, [isMounted]);

  return (
    <section className={styles.caseStudy}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.badge}>{badge}</div>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>

        <div className={styles.casesWrapper}>
          {cases.map((caseItem, index) => (
            <div
              key={index}
              className={`${styles.caseItem} ${index % 2 === 1 ? styles.reversed : ''}`}
            >
              <div className={styles.textContent}>
                <div className={styles.company}>
                  <span className={styles.companyLabel}>{labels.client}</span>
                  <span className={styles.companyName}>{caseItem.company}</span>
                </div>
                <div className={styles.solution}>
                  <span className={styles.solutionLabel}>{labels.solution}</span>
                  <span className={styles.solutionName}>{caseItem.solution}</span>
                </div>
                
                <div className={styles.descriptionWrapper}>
                  <p className={styles.description}>{caseItem.description}</p>
                  
                  {caseItem.integrations && caseItem.integrations.length > 0 && (
                    <div className={styles.integrations}>
                      <h3 className={styles.integrationsTitle}>{labels.integratedWith}</h3>
                      <div className={styles.integrationLogos}>
                        {caseItem.integrations.map((integration, idx) => {
                          // Generate integration slug for URL
                          const slug = integration.name.toLowerCase().replace(/\s+/g, '-').replace(/\./g, '');
                          
                          // Use centralized route function
                          const integrationUrl = getIntegrationOrPartnerPath(slug, locale);
                          
                          return (
                            <Link key={idx} href={integrationUrl} className={styles.integrationLogo} prefetch={true}>
                              <Image
                                src={integration.logo}
                                alt={altTexts?.[integration.name.toLowerCase().replace(/\s+/g, '-')] || `${integration.name} integration`}
                                width={120}
                                height={60}
                                style={{ objectFit: 'contain' }}
                                loading="lazy"
                              />
                              <span className={styles.integrationName}>{integration.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.videoContainer}>
                {/* ✅ ALWAYS render video with poster - instant visual! */}
                  <video
                    ref={(el) => { videoRefs.current[index] = el; }}
                    className={styles.video}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="none"
                    poster={`/videos/posters/${caseItem.videoWebm.split('/').pop()?.replace('.webm', '')}.webp`}
                    aria-label={`${caseItem.company} kiosk in action`}
                  >
                    {/* MP4 first (smaller file size) */}
                    {/* Mobile & Tablet: 540p for sharp display */}
                    <source
                      data-src={`/videos/${caseItem.videoMp4.split('/').pop()?.replace('.mp4', '')}-540p.mp4`}
                      type="video/mp4"
                      media="(max-width: 1024px)"
                    />
                    {/* Desktop: 720p */}
                    <source
                      data-src={`/videos/${caseItem.videoMp4.split('/').pop()?.replace('.mp4', '')}-720p.mp4`}
                      type="video/mp4"
                      media="(min-width: 1025px)"
                    />
                    {/* WebM fallback */}
                    <source
                      data-src={`/videos/${caseItem.videoWebm.split('/').pop()?.replace('.webm', '')}-540p.webm`}
                      type="video/webm"
                      media="(max-width: 1024px)"
                    />
                    <source
                      data-src={`/videos/${caseItem.videoWebm.split('/').pop()?.replace('.webm', '')}-720p.webm`}
                      type="video/webm"
                      media="(min-width: 1025px)"
                    />
                    Your browser does not support video.
                  </video>
                <div className={styles.videoGlow} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

